import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import axios from "axios";
import { getToken, base_url } from "../../config/config";

const GanttChart = () => {
  const chartRef = useRef();
  const [machines, setMachines] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth * 0.9,
    height: window.innerHeight * 0.7,
  });

  // Fetch machines from API
  const fetchMachines = async () => {
    try {
      const response = await axios.get(`${base_url}/v1/getAllMachines`);
      setMachines(response.data);
    } catch (error) {
      console.error("Error fetching machines:", error);
    }
  };

  // Fetch tasks (Gantt data) from API
  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${base_url}/v1/GetAllProjectPlanning`);
      if (response.data.length > 0) {
        const allTasks = response.data.flatMap((item) => item.data);
        setTasks(allTasks);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
  };

  // Get the current month's date range
  const getCurrentMonthDates = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const dates = [];
    for (
      let date = startOfMonth;
      date <= endOfMonth;
      date.setDate(date.getDate() + 1)
    ) {
      dates.push(new Date(date));
    }
    return dates;
  };

  // Update chart size based on window resize
  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth * 0.9,
        height: window.innerHeight * 0.7,
      });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    fetchMachines();
    fetchTasks();
  }, []);

  // Draw Gantt Chart
  useEffect(() => {
    if (machines.length === 0 || tasks.length === 0) return;

    const svg = d3.select(chartRef.current);
    const margin = { top: 40, right: 30, bottom: 80, left: 200 };
    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    const parseDate = d3.timeParse("%Y-%m-%dT%H:%M:%S.000Z"); // Ensure we correctly parse the full date and time

    const getEndDate = (startDate, hours) =>
      new Date(startDate.getTime() + hours * 60 * 60 * 1000);

    const machineNames = machines.map((machine) => machine.name);
    const machineToIndex = d3
      .scaleBand()
      .domain(machineNames)
      .range([0, height])
      .padding(0.1);

    const taskDates = tasks.map((task) => parseDate(task.date)); // Parse task start dates

    // Stretch the x-axis further to increase spacing between dates
    const xScale = d3
      .scaleTime()
      .domain([d3.min(taskDates), d3.max(taskDates)]) // Use the min and max of task dates for scaling
      .range([0, width * 3]); // Stretch by 3 times the width for even more room between dates

    const currentMonthDates = getCurrentMonthDates();
    const xScaleCurrentMonth = d3
      .scaleTime()
      .domain([
        currentMonthDates[0],
        currentMonthDates[currentMonthDates.length - 1],
      ])
      .range([0, width * 3]); // Stretch by 3 times the width for even more room

    svg.selectAll("*").remove();

    const chart = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const xAxisGrid = d3
      .axisBottom(xScaleCurrentMonth)
      .ticks(d3.timeDay.every(1))
      .tickSize(-height)
      .tickFormat("");

    chart
      .append("g")
      .attr("class", "x grid")
      .attr("transform", `translate(0,${height})`)
      .call(xAxisGrid)
      .selectAll(".tick line")
      .attr("stroke", "#ccc")
      .attr("stroke-dasharray", "3,3");

    const yAxisGrid = d3
      .axisLeft(machineToIndex)
      .tickSize(-width)
      .tickFormat("");

    const yAxis = chart
      .append("g")
      .attr("class", "y grid")
      .call(yAxisGrid)
      .selectAll(".tick line")
      .attr("stroke", "#ccc")
      .attr("stroke-dasharray", "3,3");

    // Increase the font size of the machine names on the left (y-axis)
    chart
      .append("g")
      .call(d3.axisLeft(machineToIndex))
      .selectAll("text")
      .style("font-size", "16px") // Increase font size here
      .style("font-weight", "bold"); // Optionally make it bold

    chart
      .append("g")
      .attr("transform", `translate(0,${height + 20})`)
      .call(
        d3
          .axisBottom(xScaleCurrentMonth)
          .ticks(d3.timeDay.every(1))
          .tickFormat(d3.timeFormat("%d")),
      )
      .selectAll("text")
      .attr("transform", "rotate(-45)")
      .style("text-anchor", "end");

    chart
      .append("g")
      .attr("transform", `translate(0,${height})`)
      .call(
        d3
          .axisBottom(xScaleCurrentMonth)
          .ticks(d3.timeDay.every(1))
          .tickFormat(d3.timeFormat("%a")),
      )
      .selectAll("text")
      .attr("transform", "rotate(0)")
      .style("text-anchor", "middle");

    // Bars (Gantt chart tasks)
    const bars = chart
      .selectAll("rect")
      .data(tasks)
      .enter()
      .append("rect")
      .attr("x", (d) => xScale(parseDate(d.date))) // Start date
      .attr("y", (d) => machineToIndex(d.machinename)) // Machine placement
      .attr(
        "width",
        (d) =>
          xScale(getEndDate(parseDate(d.date), d.hours)) -
          xScale(parseDate(d.date)),
      ) // Width based on hours
      .attr("height", machineToIndex.bandwidth() * 1.2) // Increase height
      .attr("fill", (d, i) => d3.schemeCategory10[i % 10])
      .attr("stroke", "black");

    // Add full ANR (rep) text labels inside the bars
    chart
      .selectAll("text.anr-label")
      .data(tasks)
      .enter()
      .append("text")
      .attr("class", "anr-label")
      .attr("x", (d) => xScale(parseDate(d.date)) + 10) // Adjust x position for better visibility
      .attr(
        "y",
        (d) => machineToIndex(d.machinename) + machineToIndex.bandwidth() / 1.5,
      ) // Adjust y position to center it inside the bar
      .attr("dy", ".35em")
      .text((d) => d.anr || d.orderDescription) // Adjust to show either `rep` or description
      .attr("fill", "white")
      .style("font-size", "14px")
      .style("font-weight", "bold");
  }, [machines, tasks, dimensions]);

  return (
    <div style={{ overflowX: "scroll" }}>
      {" "}
      {/* Enable horizontal scrolling */}
      <h1>Gantt Chart</h1>
      <svg
        ref={chartRef}
        width={dimensions.width * 3} // Adjust to match the stretched xScale
        height={dimensions.height}
        viewBox={`0 0 ${dimensions.width * 3} ${dimensions.height}`} // Adjust viewBox accordingly
        preserveAspectRatio='xMinYMin meet'
      />
    </div>
  );
};

export default GanttChart;
